// src/components/Header.tsx
import React, { FC, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Иконки MUI
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

// Ваш кастомный Icon
import Icon from "../UI/Icon/Icon.tsx";
import { ReactComponent as VectorLocation } from "../icons/Vector.svg"; 
import { ReactComponent as CalendarBlank } from "../icons/CalendarBlank.svg";

// Кастомный селект (для подразделений)
import Select from "../UI/Select/Select.tsx";
// Модалка создания подразделения
import CreateDivisionModal from "./CreateDivisionModal.tsx";
// Компонент UserMenu (аватар + email + меню)
import UserMenu from "./UserMenu.tsx";

// API
import { createDivision, getDivisions } from "../api/enterprise";

// Стили (HeaderContainer, Pill, HeaderText, т.д.)
import {
  HeaderContainer,
  LeftSection,
  RightSection,
  PillSelectContainer,
  Pill,
  PillSector,
  HeaderText,
  StyledIconButton,
  Time,
  Location
} from "./HeaderStyles.ts";

interface Division {
  division_id: string;
  name: string;
}
interface Enterprise {
  enterprise_id: string;
  name: string;
}
interface HeaderProps {
  enterprise?: Enterprise | null;
  divisions?: Division[];
  selectedDivisionId?: string | null;
  selectedDivisionName?: string | null;
  setDivisions?: (divisions: Division[]) => void;
  setSelectedDivisionId?: (id: string) => void;
  setSelectedDivisionName?: (name: string) => void;
  userEmail?: string;
}

// Заглушки
function getLocationMock() {
  return "Новосибирск";
}
function getCurrentTimeMock() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
}
function getRouteLabel(pathname: string, t: any): string {
  if (pathname.includes("orders")) return t("orders");
  if (pathname.includes("nomenclature")) return t("sidebar.manage_nomenclature");
  if (pathname.includes("equipment")) return t("sidebar.manage_equipment");
  if (pathname.includes("division")) return t("sidebar.manage_divisions");
  return "";
}

const Header: FC<HeaderProps> = ({
  enterprise,
  divisions = [],
  selectedDivisionId,
  setDivisions,
  setSelectedDivisionId,
  setSelectedDivisionName,
  userEmail
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Состояние выбора подразделения
  const [selectedDivision, setSelectedDivision] = useState<string>(selectedDivisionId || "");
  const [showCreateModal, setShowCreateModal] = useState(false);

  // При загрузке синхронизируем selectedDivision
  useEffect(() => {
    if (selectedDivisionId) {
      setSelectedDivision(selectedDivisionId);
    }
  }, [selectedDivisionId]);

  const handleCreateDivision = async (entId: string, divisionName: string) => {
    try {
      await createDivision(entId, divisionName);
      if (setDivisions) {
        const newDivs = await getDivisions(entId);
        setDivisions(newDivs);
      }
      setShowCreateModal(false);
    } catch (error) {
      console.error("Error creating division:", error);
    }
  };

  const handleDivisionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    setSelectedDivision(newId);
    if (newId && setSelectedDivisionId && setSelectedDivisionName && enterprise) {
      setSelectedDivisionId(newId);
      const found = divisions.find(d => d.division_id === newId);
      if (found) {
        setSelectedDivisionName(found.name);
        localStorage.setItem("selectedDivisionName", found.name);
        navigate(`/${enterprise.enterprise_id}/division/${newId}`);
      }
    }
  };

  const routeLabel = getRouteLabel(location.pathname, t);
  const loc = getLocationMock();
  const currentTime = getCurrentTimeMock();

  if (!enterprise) {
    return (
      <div style={{ padding: 16 }}>
        <HeaderText>{t("loading")}</HeaderText>
      </div>
    );
  }

  return (
    <HeaderContainer>
      {/* Левая часть */}
      <LeftSection>
        {/* Капсула для селекта подразделений */}
        <PillSelectContainer>
          {divisions.length > 0 ? (
            <Select size="M"  value={selectedDivision} onChange={handleDivisionChange}>
              <option value="" disabled>
                {t("select_division")}
              </option>
              {divisions.map(div => (
                <option key={div.division_id} value={div.division_id}>
                  {div.name}
                </option>
              ))}
            </Select>
          ) : (
            <HeaderText
              style={{ color: "#007BFF", cursor: "pointer" }}
              onClick={() => setShowCreateModal(true)}
            >
              {t("create_division")}
            </HeaderText>
          )}
        </PillSelectContainer>
        <HeaderText >/</HeaderText>
        {/* Капсула с названием текущего раздела */}
        {routeLabel && (
          <Pill>
            <HeaderText>{routeLabel}</HeaderText>
          </Pill>
        )}
      </LeftSection>

      {/* Правая часть */}
      <RightSection>
        {/* Капсула с местоположением */}
        <Location>
        <PillSector><Icon SvgComponent={VectorLocation} size={20} /* color="#6B7280" */ /></PillSector>
          <HeaderText >{loc}</HeaderText>
        </Location>

        {/* Капсула с датой/временем */}
        <Time>
          <PillSector>
            <Icon SvgComponent={CalendarBlank} size={20} />
          </PillSector>
          <HeaderText>{currentTime}</HeaderText>
        </Time>

        {/* Капсула: уведомления + UserMenu */}
        <Pill>
          <StyledIconButton>
            <NotificationsNoneIcon />
          </StyledIconButton>
        </Pill>
        <Pill>
          

          {/* Пользователь (email + стрелка) */}
          <UserMenu userEmail={userEmail} />
          {/* Можно передать onLogout, если нужно:
             <UserMenu userEmail={userEmail} onLogout={logoutFunction} />
          */}
        </Pill>
      </RightSection>

      {/* Модалка создания подразделения */}
      <CreateDivisionModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        handleCreate={handleCreateDivision}
        enterpriseId={enterprise.enterprise_id}
      />
    </HeaderContainer>
  );
};

export default Header;
